import { Box, Button } from "@mui/material";
import { useDroppable } from "@dnd-kit/core";
import { useTranslation } from "i18n-fe";
import { NEW_SECTION_ID, POOL_ID, sectionTabId } from "../util/sectionGestures";
import { POOL_TAB } from "../hooks/useClassSections";

// #3293 - the tab strip above the equitation / medal grid: one tab per
// section, "+ New section", and "Not drawn (N)". Every tab is a drop target
// (drag a rider row onto it to move the rider there), highlighted while a
// rider is held over it. `highlightId` is the keyboard path's current target,
// so the pointer and keyboard paths light the same way.
function DropTab({ id, selected, highlighted, disabled, onClick, children, testId, role = "tab" }) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled });
  const active = isOver || highlighted;
  return (
    <Button
      ref={setNodeRef}
      role={role}
      aria-selected={role === "tab" ? selected : undefined}
      data-testid={testId}
      data-drop-target={active ? "true" : undefined}
      variant={selected ? "contained" : "outlined"}
      onClick={onClick}
      sx={{
        minHeight: 48,
        minWidth: 96,
        px: 2,
        fontWeight: 700,
        textTransform: "none",
        ...(active
          ? { outline: "3px solid", outlineColor: "warning.main", bgcolor: "warning.light", color: "text.primary" }
          : {}),
      }}
    >
      {children}
    </Button>
  );
}

export default function SectionBar({
  sections,
  poolCount,
  current,
  onSelect,
  onNewSection,
  label,
  highlightId,
  locked,
}) {
  const { t } = useTranslation("score");
  return (
    <Box
      role="tablist"
      aria-label={t("classGrid.sections.barLabel")}
      data-testid="section-bar"
      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, mb: 1.5 }}
    >
      {sections.map((s) => (
        <DropTab
          key={s.number}
          id={sectionTabId(s.number)}
          selected={current === s.number}
          highlighted={highlightId === sectionTabId(s.number)}
          disabled={locked}
          onClick={() => onSelect(s.number)}
          testId={`section-tab-${s.number}`}
        >
          {label(s.number)}
          {` (${s.rides.length})`}
        </DropTab>
      ))}
      <DropTab
        id={NEW_SECTION_ID}
        selected={false}
        highlighted={highlightId === NEW_SECTION_ID}
        disabled={locked}
        role="button"
        onClick={onNewSection}
        testId="section-tab-new"
      >
        {t("classGrid.sections.newSection")}
      </DropTab>
      <DropTab
        id={POOL_ID}
        selected={current === POOL_TAB}
        highlighted={highlightId === POOL_ID}
        disabled={locked}
        onClick={() => onSelect(POOL_TAB)}
        testId="section-tab-pool"
      >
        {t("classGrid.sections.notDrawn", { count: poolCount })}
      </DropTab>
    </Box>
  );
}
